/**
 * Formatting Utilities
 * Locale-aware formatting for currency, dates, and numbers
 */

import type { SupportedLanguage } from '../i18n/config';

// Currency codes supported by the system
export type CurrencyCode = 'MYR' | 'USD' | 'EUR' | 'SGD' | 'AED' | 'GBP' | 'SAR' | 'IDR';

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  MYR: 'RM',
  USD: '$',
  EUR: '€',
  SGD: 'S$',
  AED: 'د.إ',
  GBP: '£',
  SAR: '﷼',
  IDR: 'Rp',
};

// Locale mapping for currency formatting
const LOCALE_MAP: Record<SupportedLanguage, string> = {
  'en-US': 'en-US',
  'ms-MY': 'ms-MY',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
  'ar-SA': 'ar-SA',
};

/**
 * Format currency amount with locale-aware formatting
 * @param amount - The amount to format
 * @param currency - The currency code (default: MYR)
 * @param locale - The locale for formatting (default: en-US)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: CurrencyCode = 'MYR',
  locale: SupportedLanguage = 'en-US'
): string => {
  try {
    const formatter = new Intl.NumberFormat(LOCALE_MAP[locale], {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(amount);
  } catch (error) {
    // Fallback to manual formatting if Intl fails
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol} ${amount.toFixed(2)}`;
  }
};

/**
 * Format currency amount with custom symbol (for display flexibility)
 * @param amount - The amount to format
 * @param currency - The currency code
 * @returns Formatted currency string with symbol
 */
export const formatCurrencyWithSymbol = (
  amount: number,
  currency: CurrencyCode = 'MYR'
): string => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol} ${formatNumber(amount, 2)}`;
};

/**
 * Format date with locale-aware formatting
 * @param date - The date to format (Date object or ISO string)
 * @param locale - The locale for formatting (default: en-US)
 * @param format - The format style: 'short', 'medium', 'long', 'full'
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string,
  locale: SupportedLanguage = 'en-US',
  format: 'short' | 'medium' | 'long' | 'full' = 'medium'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  try {
    const formatter = new Intl.DateTimeFormat(LOCALE_MAP[locale], {
      dateStyle: format,
    });
    return formatter.format(dateObj);
  } catch (error) {
    // Fallback to ISO format
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format date and time with locale-aware formatting
 * @param date - The date to format (Date object or ISO string)
 * @param locale - The locale for formatting (default: en-US)
 * @param dateFormat - The date format style
 * @param timeFormat - The time format style
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: Date | string,
  locale: SupportedLanguage = 'en-US',
  dateFormat: 'short' | 'medium' | 'long' | 'full' = 'medium',
  timeFormat: 'short' | 'medium' | 'long' | 'full' = 'short'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  try {
    const formatter = new Intl.DateTimeFormat(LOCALE_MAP[locale], {
      dateStyle: dateFormat,
      timeStyle: timeFormat,
    });
    return formatter.format(dateObj);
  } catch (error) {
    // Fallback
    return dateObj.toLocaleString();
  }
};

/**
 * Format time with locale-aware formatting
 * @param date - The date to format (Date object or ISO string)
 * @param locale - The locale for formatting (default: en-US)
 * @param format - The format style: 'short', 'medium', 'long', 'full'
 * @returns Formatted time string
 */
export const formatTime = (
  date: Date | string,
  locale: SupportedLanguage = 'en-US',
  format: 'short' | 'medium' | 'long' | 'full' = 'short'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  try {
    const formatter = new Intl.DateTimeFormat(LOCALE_MAP[locale], {
      timeStyle: format,
    });
    return formatter.format(dateObj);
  } catch (error) {
    // Fallback
    return dateObj.toLocaleTimeString();
  }
};

/**
 * Format number with locale-aware formatting
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @param locale - The locale for formatting (default: en-US)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number,
  decimals: number = 0,
  locale: SupportedLanguage = 'en-US'
): string => {
  try {
    const formatter = new Intl.NumberFormat(LOCALE_MAP[locale], {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(value);
  } catch (error) {
    // Fallback
    return value.toFixed(decimals);
  }
};

/**
 * Format percentage with locale-aware formatting
 * @param value - The value to format (0.75 = 75%)
 * @param decimals - Number of decimal places (default: 0)
 * @param locale - The locale for formatting (default: en-US)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 0,
  locale: SupportedLanguage = 'en-US'
): string => {
  try {
    const formatter = new Intl.NumberFormat(LOCALE_MAP[locale], {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(value);
  } catch (error) {
    // Fallback
    return `${(value * 100).toFixed(decimals)}%`;
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param date - The date to format (Date object or ISO string)
 * @param locale - The locale for formatting (default: en-US)
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (
  date: Date | string,
  locale: SupportedLanguage = 'en-US'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  try {
    const rtf = new Intl.RelativeTimeFormat(LOCALE_MAP[locale], { numeric: 'auto' });

    if (Math.abs(diffDays) >= 1) {
      return rtf.format(diffDays, 'day');
    } else if (Math.abs(diffHours) >= 1) {
      return rtf.format(diffHours, 'hour');
    } else if (Math.abs(diffMinutes) >= 1) {
      return rtf.format(diffMinutes, 'minute');
    } else {
      return rtf.format(diffSeconds, 'second');
    }
  } catch (error) {
    // Fallback
    if (Math.abs(diffDays) >= 1) {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? 's' : ''} ${diffDays > 0 ? 'from now' : 'ago'}`;
    } else if (Math.abs(diffHours) >= 1) {
      return `${Math.abs(diffHours)} hour${Math.abs(diffHours) > 1 ? 's' : ''} ${diffHours > 0 ? 'from now' : 'ago'}`;
    } else if (Math.abs(diffMinutes) >= 1) {
      return `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) > 1 ? 's' : ''} ${diffMinutes > 0 ? 'from now' : 'ago'}`;
    } else {
      return 'just now';
    }
  }
};

/**
 * Format file size in human-readable format
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};
